import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { bookingService, sessionService, expertService } from '../api/services';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Calendar, Clock, Video, Mic, MessageCircle, ChevronLeft,
  AlertCircle, Loader2, FileText, User, Star, RotateCcw,
  CheckCircle2, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

const sessionIcon = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };
const statusStyle = {
  PENDING:     'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED:   'bg-forest-50 text-forest-700 border-forest-200',
  COMPLETED:   'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED:   'bg-cream-100 text-forest-400 border-cream-200',
  RESCHEDULED: 'bg-purple-50 text-purple-700 border-purple-200',
};

const RescheduleModal = ({ booking, onClose, onDone }) => {
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(false);
  const minDate = new Date(Date.now() + 3600000).toISOString().slice(0, 16);

  const handle = async (e) => {
    e.preventDefault();
    if (!scheduledAt) return;
    setLoading(true);
    try {
      await bookingService.reschedule(booking.id, new Date(scheduledAt).toISOString());
      toast.success('Booking rescheduled! Expert will need to re-confirm.');
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reschedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-forest-900">Reschedule Booking</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-xl leading-none">✕</button>
        </div>
        <p className="text-sm text-forest-500">
          Max 2 reschedules per booking.{' '}
          <span className="font-medium text-forest-700">({Math.max(0, 2 - (booking.rescheduleCount ?? 0))} remaining)</span>
        </p>
        <form onSubmit={handle} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">New Date & Time</label>
            <input type="datetime-local" required value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} min={minDate}
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">Cancel</button>
            <button type="submit" disabled={loading || !scheduledAt}
              className="flex-1 btn-primary justify-center text-sm py-2.5 disabled:opacity-60">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Reschedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BookingDetailPage = () => {
  const { id }       = useParams();
  const { isExpert } = useAuth();
  const navigate     = useNavigate();
  const [showReschedule, setShowReschedule] = useState(false);
  const [acting, setActing]                 = useState(null);
  const [retryingTranscript, setRetryingTranscript] = useState(false);

  const { data, loading, error, refetch } = useApi(() => bookingService.getById(id), [id]);
  const booking = data?.booking ?? data?.data?.booking ?? data;

  const handleExpertRespond = async (action) => {
    setActing(action);
    try {
      await expertService.respondToBooking(id, action);
      toast.success(action === 'confirm' ? 'Booking confirmed! Client has been notified.' : 'Booking declined.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const handleCreateSession = async () => {
    setActing('session');
    try {
      // const res = await sessionService.createFromBooking(id);
      const res = await sessionService.createFromBooking(booking.id);
      const sessionId = res.data?.data?.session?.id ?? res.data?.session?.id ?? res.data?.data?.id ?? res.data?.id;
      toast.success('Session room created! Client has been notified via SMS.');
      if (sessionId) navigate(`/sessions/${sessionId}`);
      else refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create session room. Check your Daily.co API key.');
    } finally {
      setActing(null);
    }
  };

  const handleMarkComplete = async () => {
    if (!window.confirm('Mark this session as complete?')) return;
    setActing('complete');
    try {
      await sessionService.markComplete(booking.session.id);
      toast.success('Session marked as complete!');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not mark session complete');
    } finally {
      setActing(null);
    }
  };

  const handleRetryTranscript = async () => {
    if (!booking?.session?.id) return;
    setRetryingTranscript(true);
    try {
      await sessionService.retryTranscript(booking.session.id);
      toast.success('Transcript retry started — check back in a few minutes.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not retry transcript');
    } finally {
      setRetryingTranscript(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    setActing('cancel');
    try {
      await bookingService.cancel(id, '');
      toast.success('Booking cancelled.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not cancel');
    } finally {
      setActing(null);
    }
  };

  if (loading) return (
    <DashboardLayout title="Booking Detail">
      <div className="max-w-2xl space-y-4 animate-pulse">
        <div className="h-6 bg-cream-200 rounded w-32" />
        <div className="h-48 bg-white border border-cream-200 rounded-2xl" />
        <div className="h-32 bg-white border border-cream-200 rounded-2xl" />
      </div>
    </DashboardLayout>
  );

  if (error || !booking || typeof booking !== 'object' || !booking.id) return (
    <DashboardLayout title="Booking Detail">
      <div className="max-w-2xl">
        <div className="bg-white border border-cream-200 rounded-2xl p-10 text-center space-y-4">
          <AlertCircle size={36} className="mx-auto text-rust-400" />
          <h3 className="font-serif text-xl text-forest-900">Booking not found</h3>
          <p className="text-sm text-forest-500">{error || "This booking may have been removed or you don't have access."}</p>
          <Link to={isExpert ? '/expert/bookings' : '/bookings'} className="btn-primary inline-flex">← Back to Bookings</Link>
        </div>
      </div>
    </DashboardLayout>
  );

  const Icon = sessionIcon[booking.sessionType] ?? Video;

  // ✅ FIX: backend returns expertProfile and clientProfile (not expert/client)
  const expertData    = booking.expertProfile ?? booking.expert ?? {};
  const expertUser    = expertData.user ?? expertData;
  const expertName    = [expertUser.firstName ?? '', expertUser.lastName ?? ''].filter(Boolean).join(' ').trim() || '—';
  const expertEmail   = expertUser.email ?? '';
  const expertRating  = expertData.averageRating ?? expertUser.averageRating;
  const expertProfileId = expertData.id ?? booking.expertProfileId ?? '';

  const clientData    = booking.clientProfile ?? booking.client ?? {};
  const clientUser    = clientData.user ?? clientData;
  const clientName    = [clientUser.firstName ?? '', clientUser.lastName ?? ''].filter(Boolean).join(' ').trim() || '—';
  const clientEmail   = clientUser.email ?? '';
  const clientBusiness = clientData.businessName ?? clientUser.businessName ?? '';

  const date        = booking.scheduledAt ? new Date(booking.scheduledAt).toLocaleString('en-GH', { dateStyle: 'long', timeStyle: 'short' }) : '—';
  const isPending   = booking.status === 'PENDING';
  const isConfirmed = booking.status === 'CONFIRMED';
  const isCompleted = booking.status === 'COMPLETED';
  const isCancellable = isPending || isConfirmed;
  const hasSession  = !!booking.session?.id;
  const canReschedule = isCancellable && !isExpert && (booking.rescheduleCount ?? 0) < 2;

  return (
    <DashboardLayout title="Booking Detail">
      {showReschedule && <RescheduleModal booking={booking} onClose={() => setShowReschedule(false)} onDone={refetch} />}

      <div className="max-w-2xl space-y-5">
        <Link to={isExpert ? '/expert/bookings' : '/bookings'}
          className="inline-flex items-center gap-1.5 text-sm text-forest-500 hover:text-forest-800 transition-colors">
          <ChevronLeft size={15} /> Back to Bookings
        </Link>

        <div className="bg-white border border-cream-200 rounded-2xl p-6 space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-forest-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon size={22} className="text-forest-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h2 className="font-serif text-xl font-semibold text-forest-900">{booking.sessionType} Session</h2>
                <span className={`text-xs font-semibold border px-3 py-1 rounded-full flex-shrink-0 ${statusStyle[booking.status] ?? ''}`}>
                  {booking.status}
                </span>
              </div>
              {booking.level?.name && <p className="text-sm text-forest-500 mt-1">{booking.level.name}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 py-4 border-y border-cream-100">
            <div className="flex items-center gap-2.5 text-sm text-forest-600">
              <Calendar size={15} className="text-forest-400 flex-shrink-0" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-2.5 text-sm text-forest-600">
              <Clock size={15} className="text-forest-400 flex-shrink-0" />
              <span>{booking.duration} minutes</span>
            </div>
            {(booking.rescheduleCount ?? 0) > 0 && (
              <div className="flex items-center gap-2.5 text-sm text-forest-500 col-span-2">
                <RotateCcw size={15} className="text-forest-400 flex-shrink-0" />
                <span>Rescheduled {booking.rescheduleCount}× (max 2)</span>
              </div>
            )}
          </div>

          {booking.notes && (
            <div>
              <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-2">Session Notes</p>
              <p className="text-sm text-forest-700 leading-relaxed bg-cream-50 rounded-xl p-4 italic">"{booking.notes}"</p>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-cream-200 rounded-2xl p-5">
            <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-3">Expert</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold flex-shrink-0">
                {expertName !== '—' ? expertName[0]?.toUpperCase() : '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-sans font-semibold text-forest-900 text-sm">{expertName}</div>
                <div className="text-xs text-forest-400 mt-0.5 truncate">{expertEmail}</div>
                {expertRating && (
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={11} className="fill-gold-400 text-gold-400" />
                    <span className="text-xs text-forest-500">{Number(expertRating).toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
            {!isExpert && expertProfileId && (
              <Link to={`/experts/${expertProfileId}`} className="mt-3 block text-xs text-forest-500 hover:text-forest-800 font-medium transition-colors">
                View profile →
              </Link>
            )}
          </div>

          <div className="bg-white border border-cream-200 rounded-2xl p-5">
            <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-3">Client</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cream-200 rounded-xl flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-forest-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-sans font-semibold text-forest-900 text-sm">{clientName}</div>
                <div className="text-xs text-forest-400 mt-0.5 truncate">{clientEmail}</div>
                {clientBusiness && <div className="text-xs text-forest-400 truncate">{clientBusiness}</div>}
              </div>
            </div>
          </div>
        </div>

        {/* {hasSession && (
          <div className="bg-forest-50 border border-forest-200 rounded-2xl p-5">
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-3">Session Room</p>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-medium text-forest-900">Room ready</div>
                <div className="text-xs text-forest-500 mt-0.5">Status: {booking.session?.status}</div>
              </div>
              <Link to={`/sessions/${booking.session.id}`} className="btn-primary text-sm py-2 px-5">
                {booking.session?.status === 'LIVE' ? 'Rejoin →' : 'Open Session →'}
              </Link>
            </div>
          </div>
        )} */}

        {hasSession && (
  <div className="bg-forest-50 border border-forest-200 rounded-2xl p-5">
    <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-3">Session Room</p>
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div>
        <div className="text-sm font-medium text-forest-900">
          {isCompleted ? 'Session ended' : 'Room ready'}
        </div>
        <div className="text-xs text-forest-500 mt-0.5">Status: {booking.session?.status}</div>
      </div>
      {/* Only show Open Session if NOT completed */}
      {!isCompleted && (
        <Link to={`/sessions/${booking.session.id}`} className="btn-primary text-sm py-2 px-5">
          {booking.session?.status === 'LIVE' ? 'Rejoin →' : 'Open Session →'}
        </Link>
      )}
      {/* Show Watch Recording if completed */}
      {isCompleted && booking.session?.recordingUrl && (
        <Link
          to={isExpert
            ? `/expert/sessions/${booking.session.id}/recording`
            : `/sessions/${booking.session.id}/recording`}
          className="btn-primary text-sm py-2 px-5">
          🎬 Watch Recording →
        </Link>
      )}
    </div>
  </div>
)}

        {/* {isCompleted && booking.session?.transcript && (
          <div className="bg-white border border-cream-200 rounded-2xl p-5">
            <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest mb-3">Transcript</p>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-forest-700 capitalize">Status: {booking.session.transcript.status?.toLowerCase()}</div>
              {booking.session.transcript.pdfUrl && (
                <a href={booking.session.transcript.pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm border border-forest-200 text-forest-700 hover:bg-forest-50 px-4 py-2 rounded-full transition-colors font-medium">
                  <FileText size={14} /> Download PDF
                </a>
              )}
            </div>
          </div>
        )} */}

        {isCompleted && (
          <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-4">
            <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest">Session Recording & Transcript</p>

            {/* Recording */}
            {booking.session?.recordingUrl ? (
              <div className="space-y-2">
                <p className="text-xs text-forest-500 font-medium">Recording</p>
                {booking.sessionType === 'AUDIO' ? (
                  <audio controls className="w-full" src={booking.session.recordingUrl} />
                ) : (
                  <video controls className="w-full rounded-xl max-h-64 bg-black" src={booking.session.recordingUrl} />
                )}
              </div>
            ) : (
              <p className="text-sm text-forest-400">⏳ Recording is being processed, check back soon.</p>
            )}

            {/* Transcript */}
            {booking.session?.transcript && (
              <div className="space-y-2 pt-3 border-t border-cream-100">
                {(() => {
                  const t = booking.session.transcript;
                  // If content exists, treat as completed regardless of stuck status
                  const effectiveStatus = (t.pdfUrl || t.rawText) ? 'COMPLETED' : t.status;
                  const isCompleted  = effectiveStatus === 'COMPLETED';
                  const isFailed     = effectiveStatus === 'FAILED';
                  const isProcessing = effectiveStatus === 'PROCESSING';
                  return (
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className={`text-xs font-medium flex items-center gap-1.5 ${
                          isCompleted  ? 'text-forest-600' :
                          isFailed     ? 'text-rust-500'   :
                          'text-amber-600'
                        }`}>
                          {isCompleted  && <CheckCircle2 size={12} />}
                          {isFailed     && <XCircle      size={12} />}
                          {isProcessing && <Loader2      size={12} className="animate-spin" />}
                          Transcript —{' '}
                          {isCompleted ? 'Ready' : isFailed ? 'Failed' : isProcessing ? 'Processing...' : 'Pending'}
                        </p>
                        <div className="flex items-center gap-2">
                          {isFailed && (
                            <button onClick={handleRetryTranscript} disabled={retryingTranscript}
                              className="flex items-center gap-1.5 text-xs border border-amber-200 text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-full transition-colors font-medium disabled:opacity-50">
                              {retryingTranscript
                                ? <><Loader2 size={11} className="animate-spin" /> Retrying...</>
                                : <><RotateCcw size={11} /> Retry</>}
                            </button>
                          )}
                          {t.pdfUrl && (
                            <a href={t.pdfUrl} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors font-medium">
                              <FileText size={12} /> Download PDF
                            </a>
                          )}
                        </div>
                      </div>
                      {t.rawText && (
                        <div className="bg-cream-50 rounded-xl p-4 text-sm text-forest-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                          {t.rawText}
                        </div>
                      )}
                      {isFailed && !t.rawText && (
                        <p className="text-xs text-rust-400 italic">The recording may be too short or contain no speech. Click Retry to try again.</p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Full page link */}
            <button
              onClick={() => navigate(
                isExpert
                  ? `/expert/sessions/${booking.session.id}/recording`
                  : `/sessions/${booking.session.id}/recording`
              )}
              className="inline-flex items-center gap-2 text-sm text-forest-600 hover:text-forest-900 font-medium transition-colors"
            >
              🎬 View full recording page →
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {/* ── Mark Complete — shown for BOTH client and expert ── */}
          {isConfirmed && hasSession && !isCompleted && (
            <button
              onClick={handleMarkComplete}
              disabled={!!acting}
              className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {acting === 'complete'
                ? <><Loader2 size={14} className="animate-spin" /> Completing...</>
                : <><CheckCircle2 size={14} /> Mark Session Complete</>}
            </button>
          )}

          {isExpert && isPending && (
            <>
              <button onClick={() => handleExpertRespond('confirm')} disabled={!!acting}
                className="btn-primary flex items-center gap-2 disabled:opacity-60">
                {acting === 'confirm' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Confirm Booking
              </button>
              <button onClick={() => handleExpertRespond('cancel')} disabled={!!acting}
                className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border border-rust-200 text-rust-500 hover:bg-rust-500 hover:text-white hover:border-rust-500 transition-all font-medium disabled:opacity-60">
                {acting === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Decline
              </button>
            </>
          )}

          {isExpert && isConfirmed && !hasSession && (
            <button onClick={handleCreateSession} disabled={!!acting}
              className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {acting === 'session' ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
              Create Session Room
            </button>
          )}

          {canReschedule && (
            <button onClick={() => setShowReschedule(true)}
              className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border border-forest-200 text-forest-700 hover:bg-forest-50 transition-colors font-medium">
              <RotateCcw size={14} /> Reschedule
            </button>
          )}

          {isCancellable && !isExpert && (
            <button onClick={handleCancel} disabled={acting === 'cancel'}
              className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border border-rust-200 text-rust-500 hover:bg-rust-500 hover:text-white hover:border-rust-500 transition-all font-medium disabled:opacity-60">
              {acting === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : null}
              Cancel Booking
            </button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BookingDetailPage;